import { t } from 'i18next'
import React, { useEffect, useState } from 'react'
import { Input } from 'semantic-ui-react'
import { useDebounce } from 'use-lodash-debounce'
import Layout from '~/components/Layout'
import TableComponent from '~/components/TableComponent'
import i18n from '~/components/i18n'
import { StyledTab } from '~/styles/table'
import { LoginPopupsQuery } from '~/QueryHooks/LoginPopupsQuery'
import { FiltersWrapper } from '../ActivityLogs/style'
import { RouteTypes } from '~/routes/constants'

const getDefaultTableColumnHeaders = () => [
  {
    name: i18n.t('Name'),
    key: 'name'
  },
  {
    name: i18n.t('Accepted'),
    key: 'accepted'
  },
  {
    name: i18n.t('Rejected'),
    key: 'rejected'
  },
  {
    name: i18n.t('Number of changes'),
    key: 'total'
  },
  {
    name: i18n.t('Reviewed by'),
    key: 'reviewedBy'
  },
  {
    name: i18n.t('Reviewed at'),
    key: 'reviewedAt',
    type: 'date',
    format: 'DD.MM.YYYY HH:mm'
  },
  {
    name: i18n.t('Created at'),
    key: 'createdAt',
    type: 'date',
    format: 'DD.MM.YYYY HH:mm',
    sorted: 'desc'
  },
  {
    name: i18n.t('Actions')
  }
]

const ChangeRequest: React.FunctionComponent = () => {
  const [tableColumnHeaders, setTableColumnHeaders] = useState(getDefaultTableColumnHeaders())
  const [search, setSearch] = useState('')
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 20,
    status: 0,
    search: ''
  })

  const actions = [
    {
      popupMessage: i18n.t('Review'),
      type: 'link',
      name: 'eye',
      size: 'large',
      link: RouteTypes.DAIRY_FACTORY_CHANGE_REQUEST_REVIEW,
      disableForLightUser: true
    }
  ]

  const debouncedSearchValue = useDebounce(search, 300)

  const {
    isLoading: isBusyData,
    data: changeRequestsData,
    refetch: refetchChangeRequests,
    isFetching: isFetchingData
  } = LoginPopupsQuery.getChangeDataRequests({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  })

  const {
    isLoading: isBusyCount,
    data: changeRequestsCounts,
    refetch: refetchChangeCounts,
    isFetching: isFetchingCount
  } = LoginPopupsQuery.getChangeDataCounts({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  })

  const {
    data: indexData,
    isLoading: isBusyOptions,
    refetch: refetchOptions,
    isFetching: isFetchingOptions
  } = LoginPopupsQuery.getChangeDataRequestsIndexData(dataFiltered)

  const statuses = indexData?.statuses || []

  useEffect(() => {
    setTableColumnHeaders(getDefaultTableColumnHeaders())
    refetchOptions()
  }, [t])

  useEffect(() => {
    refetchChangeRequests()
    refetchChangeCounts()
  }, [dataFiltered])

  useEffect(() => {
    if (statuses.length) {
      setActiveTabIndex(statuses.findIndex(status => parseInt(status.key) === dataFiltered.status))
    }
  }, [indexData])

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedSearchValue, page: 0 })
  }, [debouncedSearchValue])

  function renderSearch() {
    return (
      <FiltersWrapper>
        <Input
          icon={{ name: 'search', circular: true, link: true }}
          placeholder={t('Search')}
          value={search}
          onChange={(e, data) => setSearch(data.value)}
        />
      </FiltersWrapper>
    )
  }

  function renderTable() {
    const { filtered, rows } = changeRequestsData ? changeRequestsData : { filtered: 0, rows: [] }
    return <TableComponent
      isFetchingData={isFetchingData || isFetchingOptions}
      animated
      tableColumnHeaders={tableColumnHeaders}
      dataFiltered={dataFiltered}
      handleChangeDataFiltered={(data, headers) => {
        setTableColumnHeaders(headers)
        setDataFiltered(data)
      }}
      actions={actions}
      rows={rows}
      total={filtered}
    />
  }

  function handleTabChange(activeIndex) {
    if (!isFetchingData) {
      setActiveTabIndex(activeIndex)
      const status = statuses.find((_, indexOption) => indexOption === activeIndex)
      setDataFiltered({
        ...dataFiltered,
        status: parseInt(status?.key),
        page: 0
      })
    }
  }

  function renderPanes() {
    const panes = statuses.map((status) => {
      const countItem = changeRequestsCounts?.find(count => count.status === parseInt(status.key))
      return {
        menuItem: `${status.label} (${countItem?.count})`,
        render: () => renderTable()
      }
    })

    return (
      <StyledTab
        menu={{ secondary: true, pointing: true }}
        panes={panes}
        activeIndex={activeTabIndex}
        onTabChange={(_, data) => handleTabChange(data.activeIndex)}
      />
    )
  }

  return (
    <Layout
      title={t('Change request')}
      isBusy={isBusyData || isBusyOptions || isBusyCount}
      segmented
    >
      {renderPanes()}
    </Layout>
  )
}

export default ChangeRequest
